import { FC, useEffect, useRef, useState } from 'react';
import { SpaceKind, TreeSpaceKind } from 'shared';
import { twMerge } from 'tailwind-merge';

import { GlobeIcon, WorkspaceFolderIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { ModalWrapper } from '../../../../components/modal-wrapper/index';
import { useLocale, useReactForm } from '../../../../hooks/index';
import { useCreateDataRoom } from '../../../../hooks/space/index';
import { StringKey } from '../../../../lang/index';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { useCreateWorkspace } from '../../hooks/use-create-workspace.hook';
import { useCreatableSpaceKinds } from '../../hooks/use-space-kinds.hook';
import { DataRoomModalStore } from '../../store/data-room-modal.store';
import {
  CreateWorkspaceForm,
  createWorkspaceSchema,
  toCreateWorkspacePayload,
} from '../../validations/create-workspace.validation';
import { SpaceDetailsFields } from './space-details-fields';

enum CreateSpaceStep {
  DETAILS = 'details',
  TYPE = 'type',
}

const SPACE_KINDS: Record<
  TreeSpaceKind,
  {
    description: StringKey;
    descriptionClassName?: string;
    descriptionPlaceholder: StringKey;
    nameLabel: StringKey;
    optionTitle: StringKey;
    subtitle: StringKey;
    title: StringKey;
  }
> = {
  [SpaceKind.DATA_ROOM]: {
    description: 'DATA_ROOM_DESCRIPTION',
    descriptionClassName: 'max-w-[184.5px]',
    descriptionPlaceholder: 'ENTER_DATA_ROOM_DESCRIPTION',
    nameLabel: 'DATA_ROOM_NAME',
    optionTitle: 'DATA_ROOM',
    subtitle: 'SET_UP_DATA_ROOM',
    title: 'NEW_DATA_ROOM',
  },
  [SpaceKind.WORKSPACE]: {
    description: 'WORKSPACE_DESCRIPTION',
    descriptionPlaceholder: 'ENTER_WORKSPACE_DESCRIPTION',
    nameLabel: 'INTERNAL_WORKSPACE_NAME',
    optionTitle: 'WORKSPACE',
    subtitle: 'SET_UP_INTERNAL_WORKSPACE',
    title: 'NEW_INTERNAL_WORKSPACE',
  },
};

type CreateSpaceWizard = {
  kind: TreeSpaceKind;
  step: CreateSpaceStep;
};

const initialWizard: CreateSpaceWizard = {
  kind: SpaceKind.DATA_ROOM,
  step: CreateSpaceStep.TYPE,
};

const initialDetails: CreateWorkspaceForm = { description: '', name: '' };

const CreateSpaceProgress: FC<{ step: CreateSpaceStep }> = ({ step }) => {
  const isDetails = step === CreateSpaceStep.DETAILS;

  return (
    <div className="flex w-[60px] gap-1">
      <div
        className={twMerge(
          'h-[3px] flex-1 rounded-[25px]',
          isDetails
            ? 'bg-forest-500'
            : 'bg-gradient-to-r from-forest-500 from-[38.5%] to-forest-300',
        )}
      />
      <div
        className={twMerge(
          'h-[3px] flex-1 rounded-[25px]',
          isDetails ? 'bg-gradient-to-r from-forest-500 from-[38.5%] to-forest-300' : 'bg-gray-100',
        )}
      />
    </div>
  );
};

const SpaceKindIcon: FC<{ kind: TreeSpaceKind }> = ({ kind }) => {
  if (kind === SpaceKind.DATA_ROOM) {
    return <GlobeIcon className="size-14" height={56} width={56} />;
  }

  return <WorkspaceFolderIcon className="size-14" height={56} width={56} />;
};

const SpaceKindCard: FC<{
  isSelected: boolean;
  kind: TreeSpaceKind;
  onSelect: (kind: TreeSpaceKind) => void;
}> = ({ isSelected, kind, onSelect }) => {
  const copy = SPACE_KINDS[kind];

  return (
    <button
      className={twMerge(
        'relative flex h-[222.5px] min-w-0 flex-1 flex-col items-start overflow-hidden rounded-lg bg-white px-4 pt-2',
        isSelected ? 'border-2 border-brand-700' : 'border border-gray-100',
      )}
      onClick={() => onSelect(kind)}
      type="button"
    >
      {isSelected && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,#EAF2FF,transparent_62%),radial-gradient(circle_at_100%_100%,#FEF0C7,transparent_58%)]"
        />
      )}
      <span className="relative flex min-h-px w-full flex-1 flex-col items-center justify-center gap-2">
        <SpaceKindIcon kind={kind} />
        <span
          className={twMerge(
            'text-base font-semibold leading-8',
            isSelected ? 'text-brand-700' : 'text-gray-700',
          )}
        >
          <AppFormattedMessage id={copy.optionTitle} />
        </span>
        <span
          className={twMerge(
            'w-full text-center text-xs font-medium leading-[18px] text-gray-500',
            copy.descriptionClassName,
          )}
        >
          <AppFormattedMessage id={copy.description} />
        </span>
      </span>
    </button>
  );
};

const StepTitle: FC<{ subtitle: StringKey; title: StringKey }> = ({ subtitle, title }) => (
  <div className="flex flex-col gap-1">
    <h2 className="text-xl font-semibold leading-8 text-gray-700">
      <AppFormattedMessage id={title} />
    </h2>
    <p className="text-xs font-[450] leading-4 text-gray-500">
      <AppFormattedMessage id={subtitle} />
    </p>
  </div>
);

export const CreateSpaceModal: FC = () => {
  const isOpen = DataRoomModalStore(({ isOpen }) => isOpen);
  const type = DataRoomModalStore(({ type }) => type);
  const handleAccept = DataRoomModalStore(({ handleAccept }) => handleAccept);
  const handleDismiss = DataRoomModalStore(({ handleDismiss }) => handleDismiss);
  const { messagesLocale } = useLocale();
  const [wizard, setWizard] = useState(initialWizard);
  const creatableKinds = useCreatableSpaceKinds();
  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
    setError,
  } = useReactForm({ defaultValues: initialDetails, schema: createWorkspaceSchema });
  const { createWorkspace, isPending: isWorkspacePending } = useCreateWorkspace({
    onDuplicateName: () =>
      setError('name', { message: messagesLocale['WORKSPACE_NAME_ALREADY_EXISTS'] }),
    onSuccess: handleAccept,
  });
  const { createDataRoom, isPending: isDataRoomPending } = useCreateDataRoom({
    onDuplicateName: () =>
      setError('name', { message: messagesLocale['DATA_ROOM_NAME_ALREADY_EXISTS'] }),
  });
  const isPending = isWorkspacePending || isDataRoomPending;
  // 7. Create Action: the picker offers only what the person may create, and is skipped when that
  // is a single kind. The fallback picks the kind the modal opens on; one taken away while it is
  // open closes it instead (below).
  const hasTypeStep = creatableKinds.length > 1;
  const kind = creatableKinds.includes(wizard.kind) ? wizard.kind : creatableKinds[0];
  const step = hasTypeStep ? wizard.step : CreateSpaceStep.DETAILS;
  const isCreateSpace = isOpen && type === DataRoomModalType.CREATE_SPACE;
  const shownKindRef = useRef<TreeSpaceKind | undefined>(undefined);

  // An admin can take the shown kind away while this is open. Falling back would quietly swap the
  // form to another kind, or draw nothing over a store that stays open and reappears once access
  // returns — so the modal closes, unless a create is in flight and its answer will close it.
  useEffect(() => {
    if (!isCreateSpace) {
      shownKindRef.current = undefined;

      return;
    }

    const shown = shownKindRef.current;

    if (!kind || (shown && !creatableKinds.includes(shown))) {
      if (!isPending) handleDismiss();

      return;
    }

    shownKindRef.current = kind;
  }, [creatableKinds, handleDismiss, isCreateSpace, isPending, kind]);

  // Reset on close, not on open: resetting on open would flash the previous values for one frame.
  useEffect(() => {
    if (isOpen) {
      return;
    }

    setWizard(initialWizard);
    reset(initialDetails);
  }, [isOpen, reset]);

  if (type !== DataRoomModalType.CREATE_SPACE || !kind) {
    return null;
  }

  const isTypeStep = step === CreateSpaceStep.TYPE;
  const isWorkspace = kind === SpaceKind.WORKSPACE;
  const details = SPACE_KINDS[kind];

  // A pending create cannot be dismissed, so a late response never closes a different modal.
  const dismiss = () => {
    if (!isPending) handleDismiss();
  };
  const canSubmit = !isTypeStep && isValid && !isPending;

  // Both kinds share the name and description rules, so one form validates both.
  const submit = () => {
    void handleSubmit((values) => {
      if (isWorkspace) {
        createWorkspace(toCreateWorkspacePayload(values));

        return;
      }

      createDataRoom(
        { name: values.name, ...(values.description ? { description: values.description } : {}) },
        { onSuccess: handleAccept },
      );
    })();
  };

  return (
    <ModalWrapper
      className="w-full max-w-[580px] shadow-none"
      control={{ onOpenChange: (open) => !open && dismiss(), open: isOpen }}
      onBgClick={dismiss}
      side="CENTER"
    >
      {/* A form, so that Enter in the field creates as the button does, and never while it cannot. */}
      <form
        className="flex w-full flex-col gap-8 overflow-hidden rounded bg-white p-6"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();

          if (canSubmit) submit();
        }}
      >
        <div className="flex flex-col gap-3">
          <CreateSpaceProgress step={step} />
          <div className="duration-200 animate-in fade-in-0" key={step}>
            {isTypeStep ? (
              <StepTitle subtitle="CHOOSE_SPACE_TYPE" title="CREATE_A_SPACE" />
            ) : (
              <StepTitle subtitle={details.subtitle} title={details.title} />
            )}
          </div>
        </div>

        <div className="duration-200 animate-in fade-in-0" key={`${step}-body`}>
          {isTypeStep ? (
            <div className="flex gap-4">
              {creatableKinds.map((option) => (
                <SpaceKindCard
                  isSelected={kind === option}
                  key={option}
                  kind={option}
                  onSelect={(nextKind) => setWizard((current) => ({ ...current, kind: nextKind }))}
                />
              ))}
            </div>
          ) : (
            <SpaceDetailsFields
              control={control}
              descriptionLabel={details.descriptionPlaceholder}
              nameLabel={details.nameLabel}
            />
          )}
        </div>

        <div className="flex items-center justify-between">
          <Button
            className="h-11 w-fit border border-gray-300 px-6 text-sm font-[450] text-gray-700"
            disabled={isPending}
            onClick={dismiss}
            styleType="DEFAULT_ROUNDED"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          {isTypeStep ? (
            <Button
              className="h-11 w-fit px-6"
              onClick={() =>
                setWizard((current) => ({ ...current, step: CreateSpaceStep.DETAILS }))
              }
            >
              <AppFormattedMessage id="CONTINUE" />
            </Button>
          ) : (
            <div className="flex items-center gap-3">
              {hasTypeStep && (
                <Button
                  className="h-11 w-fit border border-gray-300 px-6 text-sm font-[450] text-gray-700"
                  onClick={() =>
                    setWizard((current) => ({ ...current, step: CreateSpaceStep.TYPE }))
                  }
                  styleType="DEFAULT_ROUNDED"
                >
                  <AppFormattedMessage id="BACK" />
                </Button>
              )}
              <Button
                className="h-11 w-fit px-6"
                disabled={!canSubmit}
                isLoading={isPending}
                type="submit"
              >
                <AppFormattedMessage id="CREATE" />
              </Button>
            </div>
          )}
        </div>
      </form>
    </ModalWrapper>
  );
};
